import React, { useState } from "react";
import { toast } from "sonner";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function DeleteAccountCard() {
  const { lang } = useI18n();
  const id = lang === "id";
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const needsPassword = user?.auth_provider !== "google";
  if (user?.role === "admin") return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.delete("/account", { data: { password, confirm } });
      toast.success(id ? "Akunmu sudah dihapus." : "Your account has been deleted.");
      setTimeout(() => window.location.replace("/"), 600);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6" data-testid="delete-account-card">
      <h3 className="flex items-center gap-2 font-display font-semibold text-destructive"><Trash2 className="h-4 w-4" /> {id ? "Hapus Akun" : "Delete Account"}</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        {id ? "Menghapus akun dan semua datamu secara permanen: latihan, check-in, metrik tubuh, foto, postingan, dan pesan. Ekspor datamu dulu jika perlu." : "Permanently delete your account and all your data: workouts, check-ins, body metrics, photos, posts, and messages. Export your data first if needed."}
      </p>
      <Button variant="outline" onClick={() => setOpen(true)} className="mt-4 w-full gap-2 border-destructive/40 text-destructive hover:bg-destructive/10" data-testid="delete-account-btn">
        <Trash2 className="h-4 w-4" /> {id ? "Hapus akun saya" : "Delete my account"}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPassword(""); setConfirm(""); } }}>
        <DialogContent className="max-w-sm" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg text-destructive"><AlertTriangle className="h-5 w-5" /> {id ? "Hapus akun permanen?" : "Delete account permanently?"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{id ? "Tindakan ini tidak bisa dibatalkan. Semua data akan hilang selamanya." : "This cannot be undone. All data will be gone forever."}</p>
          {needsPassword && (
            <div>
              <Label htmlFor="del-pass">{id ? "Password" : "Password"}</Label>
              <Input id="del-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" data-testid="delete-account-password" />
            </div>
          )}
          <div>
            <Label htmlFor="del-confirm">{id ? "Ketik HAPUS untuk konfirmasi" : "Type HAPUS to confirm"}</Label>
            <Input id="del-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="HAPUS" className="mt-1.5" data-testid="delete-account-confirm" />
          </div>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row-reverse">
            <Button variant="destructive" onClick={submit} disabled={busy || confirm.trim().toUpperCase() !== "HAPUS" || (needsPassword && !password)} className="flex-1 gap-2" data-testid="delete-account-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {id ? "Hapus permanen" : "Delete permanently"}
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1" data-testid="delete-account-cancel">{id ? "Batal" : "Cancel"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
